import { NAV } from './nav';

// WO-13 — the Arabic pass. `nav.ts` is generated by tools/mockup-extract from the mockup's NAV, which carries an Arabic
// name for each *group* but not for each *item*, so the sidebar and the crumbs read English inside an otherwise Arabic
// shell. This table supplies the missing half, keyed by route so it cannot drift from a label rewording; a route with no
// entry falls back to the English label (and navAr.test.ts fails, so a new nav item cannot ship untranslated).
// Vocabulary follows the department's own words: مذكرة استلام (RV), بطاقة صالح / غير صالح للاستخدام (F04 / F03),
// مذكرة صرف (issue note), خطابات الإعارة (loan letters).
const NAV_ITEM_AR: Readonly<Record<string, string>> = {
  '/dash/stores': 'عمليات المستودعات',
  '/dash/proc': 'المشتريات',
  '/dash/mro': 'الإصلاح والعمرة',
  '/dash/loans': 'الإعارات',
  '/dash/quality': 'الجودة والمتابعة',
  '/dash/mgmt': 'الإدارة العليا',
  '/inv/stock': 'الرصيد المتوفر',
  '/inv/items': 'الأصناف المخزنية (الأرقام التسلسلية والتشغيلات)',
  '/inv/ledger': 'سجل حركة المخزون',
  '/inv/counter': 'نقطة الصرف (مسح)',
  '/inv/issue': 'طلب مواد',
  '/inv/issues': 'مذكرات الصرف',
  '/inv/returns': 'مذكرات الإرجاع',
  '/inv/tags': 'البطاقات (صالح · غير صالح)',
  '/inv/transfers': 'التحويلات',
  '/inv/adjustments': 'التسويات',
  '/inv/count': 'الجرد',
  '/inv/counts': 'سجلات الجرد',
  '/flow': 'العملية كاملة',
  '/help': 'المساعدة والتدريب',
  '/rpt/reports': 'التقارير',
  '/admin/themes': 'المظاهر',
  '/inv/labels': 'الملصقات',
  '/inv/scan': 'وضع المسح (الهاتف)',
  '/dmd/list': 'نماذج الطلب',
  '/dmd/generate': 'التوليد من الحدود الدنيا',
  '/dmd/board': 'لوحة الطلبات',
  '/proc/rfqs': 'خطابات طلب عروض أسعار الشراء',
  '/proc/quotes': 'عروض أسعار المورّدين',
  '/proc/compare': 'مقارنة العروض',
  '/proc/pos': 'أوامر الشراء',
  '/proc/tracker': 'متابعة أوامر الشراء',
  '/rcv/list': 'مذكرات الاستلام (RV)',
  '/rcv/inspect': 'الفحص',
  '/rcv/putaway': 'التخزين (مسح)',
  '/rcv/disc': 'المخالفات',
  '/mro/backlog': 'متراكم غير الصالح',
  '/mro/rfqs': 'خطابات طلب عروض أسعار الإصلاح',
  '/mro/ros': 'أوامر الإصلاح',
  '/mro/dns': 'مذكرات التسليم',
  '/mro/vendor': 'متابعة ما لدى المورّدين',
  '/mro/warranty': 'مطالبات الضمان',
  '/mro/cal': 'المعايرة',
  '/loan/list': 'الإعارات',
  '/loan/request': 'طلب إعارة (نستعير)',
  '/loan/issue': 'إعارة صادرة (نُعير)',
  '/loan/outstanding': 'الإعارات القائمة',
  '/mon/shelf': 'العمر التخزيني',
  '/mon/cal': 'المعايرة المستحقة',
  '/mon/po': 'أوامر الشراء المفتوحة',
  '/mon/ro': 'أوامر الإصلاح المفتوحة',
  '/mon/disc': 'المخالفات',
  '/mon/wty': 'الضمان',
  '/mon/loan': 'الإعارات المستحقة',
  '/mon/alerts': 'صندوق التنبيهات',
  '/wf/inbox': 'اعتماداتي',
  '/wf/history': 'سجل الاعتمادات',
  '/wf/delegations': 'التفويضات',
  '/wf/defs': 'تعريفات مسارات الاعتماد',
  '/wf/matrix': 'مصفوفة الاعتمادات',
  '/mdm/parts': 'القطع',
  '/mdm/partners': 'المورّدون والشركاء',
  '/mdm/aircraft': 'الطائرات والمحركات',
  '/mdm/locations': 'المستودعات والمواقع',
  '/mdm/lookups': 'القوائم المرجعية',
  '/mdm/wo': 'أوامر العمل',
  '/admin/users': 'المستخدمون',
  '/admin/roles': 'الأدوار والصلاحيات',
  '/admin/series': 'متسلسلات الترقيم',
  '/admin/company': 'الجهة والمخوّلون بالتوقيع',
  '/admin/settings': 'الإعدادات',
  '/admin/labels': 'قوالب الملصقات والطابعات',
  '/admin/alerts': 'قواعد التنبيه',
  '/admin/audit': 'سجل التدقيق',
  '/admin/import': 'استيراد البيانات',
};

/** The nav item's label in the current language — English exactly as the mockup writes it, Arabic from the table above. */
export const navItemLabel = (route: string, label: string, lang: string): string => (lang === 'ar' ? (NAV_ITEM_AR[route] ?? label) : label);

/** Routes the table still owes an Arabic label (empty on a healthy build — navAr.test.ts asserts it). */
export const untranslatedNavRoutes = (): string[] => NAV.flatMap((g) => g.items).filter((i) => !(i.route in NAV_ITEM_AR)).map((i) => i.route);
